import React from "react";

/**
 * BulbMark — Cashflow's app icon as an inline SVG.
 *
 * Designed for use at any size; props let callers tune the
 * "lit" feel (animated rays around the bulb).
 *
 * Filled with amber gradient, friendly proportions (slightly
 * pear-shaped), with a visible filament squiggle that reads
 * even at small sizes.
 */
let _bulbId = 0;
const nextBulbId = () => `bulb-grad-${++_bulbId}`;

const BulbMark = ({ size = 64, glow = false, className = "" }) => {
  const gradientIdRef = React.useRef(null);
  if (gradientIdRef.current === null) {
    gradientIdRef.current = nextBulbId();
  }
  const gradientId = gradientIdRef.current;
  return (
    <span
      className={`bulb-mark ${glow ? "bulb-mark--glow" : ""} ${className}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {glow && (
        <span className="bulb-mark__rays">
          {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => (
            <span
              key={deg}
              className="bulb-mark__ray"
              style={{
                transform: `rotate(${deg}deg)`,
                animationDelay: `${i * 0.15}s`,
              }}
            />
          ))}
        </span>
      )}
      <svg
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Glass dome — pear-shaped bulb body */}
        <path
          d="M32 4 C 19 4 9 14 9 27 C 9 35 13 41 18 45 L 18 49 L 46 49 L 46 45 C 51 41 55 35 55 27 C 55 14 45 4 32 4 Z"
          fill={`url(#${gradientId})`}
        />

        {/* Soft top-left highlight (glass reflection) */}
        <ellipse
          cx="22"
          cy="17"
          rx="5"
          ry="7"
          fill="white"
          opacity="0.4"
        />

        {/* Tungsten filament — zigzag squiggle */}
        <path
          d="M22 28 Q 26 22 30 28 Q 34 34 38 28 Q 42 22 42 28"
          stroke="white"
          strokeWidth="2.4"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.85"
        />

        {/* Filament posts down to base */}
        <line x1="22" y1="28" x2="22" y2="44" stroke="white" strokeWidth="1.6" opacity="0.55" strokeLinecap="round" />
        <line x1="42" y1="28" x2="42" y2="44" stroke="white" strokeWidth="1.6" opacity="0.55" strokeLinecap="round" />

        {/* Screw base bands — stacked, narrowing */}
        <rect x="22" y="50" width="20" height="2.4" rx="1" fill="#3D2A0E" opacity="0.85" />
        <rect x="23" y="54" width="18" height="2.4" rx="1" fill="#3D2A0E" opacity="0.7" />
        <rect x="24" y="58" width="16" height="2.4" rx="1" fill="#3D2A0E" opacity="0.55" />

        {/* Contact point */}
        <ellipse cx="32" cy="62" rx="3" ry="1.1" fill="#3D2A0E" opacity="0.6" />

        <defs>
          <linearGradient
            id={gradientId}
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor="#FFD43A" />
            <stop offset="40%" stopColor="#FFC107" />
            <stop offset="100%" stopColor="#B68800" />
          </linearGradient>
        </defs>
      </svg>
    </span>
  );
};

export default BulbMark;
